<script setup>
import { Icon } from '@iconify/vue2'
import { computed } from 'vue'
import { vMaska } from 'maska/vue'

const props = defineProps({
	label: {
		type: String,
		default: '',
	},
	search: {
		type: Boolean,
		default: false,
	},
	placeholder: {
		type: String,
		default: '',
	},
	readonly: {
		type: Boolean,
		default: false,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	type: {
		type: String,
		default: 'text',
	},
	id: String,
	error: {
		type: Boolean,
		default: false,
	},
	success: {
		type: Boolean,
		default: false,
	},
	message: {
		type: String,
	},
	value: {
		type: [String, Number],
	},
	mask: {
		type: [String, Array],
	},
})

const input = computed({
	get() {
		return props.value
	},
	set(v) {
		emit('input', v)
	},
})

const emit = defineEmits(['clear'])
</script>

<template>
	<div class="container_input">
		<div
			v-if="!search"
			class="flex gap-1 items-center mb-1"
		>
			<label
				class="!mb-0 text-label"
				for="input"
				>{{ label }}</label
			>
			<slot name="tooltip" />
		</div>
		<div
			class="input_group"
			:class="{ readonly: readonly, success, 'sf-error': error, search }"
		>
			<slot name="icon-first" />
			<input
				:id="id || input"
				v-model="input"
				class="rounded-full"
				:type="type"
				name="input"
				:readonly="readonly"
				:disabled="disabled"
				:placeholder="placeholder"
				:class="{ placeholder: placeholder }"
				@wheel="type === 'number' ? $event.target.blur() : null"
				v-maska="mask"
			/>
			<slot name="icon" />
			<Icon
				v-if="success"
				icon="ic:baseline-check"
				class="text-green-success-dark h-fit text-base"
			/>
			<Icon
				v-if="error"
				icon="ph:warning-fill"
				class="text-red-danger h-fit text-base"
			/>
			<Icon
				v-if="search && !input"
				icon="tabler:search"
				class="text-blue-base h-fit text-base"
			/>
			<div
				v-if="search && input"
				@click="emit('clear')"
			>
				<Icon
					icon="mingcute:close-circle-fill"
					class="text-blue-base cursor-pointer h-fit text-base"
				/>
			</div>
		</div>
		<p
			v-if="message"
			:class="{ 'sf-error': error, success }"
		>
			{{ message }}
		</p>
	</div>
</template>

<style scoped>
p.success {
	@apply text-green-success-dark;
}

p.sf-error {
	@apply text-red-danger;
}

input::placeholder {
	@apply text-sm text-[#737576];
}

.container_input {
	@apply flex flex-col;
}

.input_group {
	@apply border border-[#737576] rounded-[4px] flex items-center px-2 bg-white;
}

.input_group.search {
	@apply rounded-full;
}

.input_group.sf-error {
	@apply border-red-danger;
}

.input_group.success {
	@apply border-green-success-dark;
}

.input_group.readonly {
	@apply border-none p-0 caret-transparent;
}

input {
	@apply border-none leading-7 w-full;
}

input:focus-visible {
	@apply outline-none;
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
	-webkit-box-shadow: 0 0 0 30px white inset !important;
}

input.xs {
	@apply w-[2.66vw];
}

input.sm {
	@apply w-[3.23vw];
}

input.base {
	@apply w-[5.47vw];
}

input.xl {
	@apply w-[19.79vw];
}

input.full {
	@apply w-full;
}

/* label {
  @apply uppercase text-[#464947] font-semibold text-sm mb-1
} */
</style>
